import * as React from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Trash2 } from "lucide-react"

import { DataState } from "@/components/DataState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useToast } from "@/components/ui/use-toast"
import { t } from "@/lib/i18n"
import { onWriteError } from "@/lib/writeErrors"

import { borrarContacto, crearContacto, fetchContactos } from "@/routes/masters/api"

/**
 * LOS CONTACTOS DE UN CLIENTE O PROVEEDOR: más de uno, con quién es cada uno.
 *
 * Cesar, 2026-10-04: «poner los contactos del cliente (más de uno)». No es el
 * correo de cobranza —ése es un buzón y vive en la ficha— sino las PERSONAS:
 * quién firma, quién aprueba, a quién se llama cuando algo no cuadra.
 */
export function ContactosDialog({
  open, onOpenChange, partyId, nombre, puedeEditar,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  partyId: string
  nombre: string
  puedeEditar: boolean
}) {
  const qc = useQueryClient()
  const { toast } = useToast()
  const contactos = useQuery({
    queryKey: ["contactos", partyId],
    queryFn: () => fetchContactos(partyId),
    enabled: open && Boolean(partyId),
  })
  const [nuevo, setNuevo] = React.useState({ nombre: "", email: "", telefono: "", cargo: "", principal: false })

  const crear = useMutation({
    mutationFn: () => crearContacto(partyId, nuevo),
    onSuccess: () => {
      setNuevo({ nombre: "", email: "", telefono: "", cargo: "", principal: false })
      void qc.invalidateQueries({ queryKey: ["contactos", partyId] })
      void qc.invalidateQueries({ queryKey: ["clients"] })
      void qc.invalidateQueries({ queryKey: ["suppliers"] })
    },
    onError: onWriteError("el contacto"),
  })
  const borrar = useMutation({
    mutationFn: (id: string) => borrarContacto(partyId, id),
    onSuccess: () => {
      toast({ title: "Contacto quitado" })
      void qc.invalidateQueries({ queryKey: ["contactos", partyId] })
      void qc.invalidateQueries({ queryKey: ["clients"] })
      void qc.invalidateQueries({ queryKey: ["suppliers"] })
    },
    onError: onWriteError("el contacto", "quitar"),
  })

  const lista = contactos.data ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("Contactos")} · {nombre}</DialogTitle>
          <DialogDescription>
            {t("Las personas de este lado: quién firma, quién aprueba, a quién llamar. El correo al que se le reclama va en la ficha, no aquí.")}
          </DialogDescription>
        </DialogHeader>

        <DataState
          loading={contactos.isLoading}
          error={contactos.error}
          empty={lista.length === 0}
          emptyMessage={t("Todavía no hay contactos.")}
          emptyHint={t("Añade al menos a quien aprueba tus facturas.")}
          onRetry={() => void contactos.refetch()}
          rows={2}
        >
          <ul className="divide-y rounded-md border">
            {lista.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs">
                <span className="font-medium">{c.nombre}</span>
                {c.cargo ? <span className="text-muted-foreground">{c.cargo}</span> : null}
                {c.email ? <a className="underline-offset-2 hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : null}
                {c.telefono ? <span className="code text-2xs">{c.telefono}</span> : null}
                {c.principal ? <Badge variant="brand-soft" estado>{t("principal")}</Badge> : null}
                {puedeEditar ? (
                  <Button size="xs" variant="ghost" className="ml-auto" onClick={() => borrar.mutate(c.id)}
                          disabled={borrar.isPending} title={t("Quitar")}>
                    <Trash2 />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </DataState>

        {puedeEditar ? (
          <form
            className="grid grid-cols-2 gap-2 rounded-md border bg-surface-sunken p-3"
            onSubmit={(e) => { e.preventDefault(); if (nuevo.nombre.trim()) crear.mutate() }}
          >
            <div className="space-y-1">
              <Label htmlFor="ct-nombre">{t("Nombre *")}</Label>
              <Input id="ct-nombre" value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ct-cargo">{t("Cargo")}</Label>
              <Input id="ct-cargo" value={nuevo.cargo} onChange={(e) => setNuevo({ ...nuevo, cargo: e.target.value })} placeholder={t("Gerente de finanzas")} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ct-email">{t("Correo")}</Label>
              <Input id="ct-email" type="email" value={nuevo.email} onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ct-tel">{t("Teléfono")}</Label>
              <Input id="ct-tel" value={nuevo.telefono} onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })} />
            </div>
            <label className="col-span-2 flex items-center gap-2 text-xs">
              <Checkbox checked={nuevo.principal} onCheckedChange={(v) => setNuevo({ ...nuevo, principal: v === true })} />
              {t("Es el contacto principal")}
            </label>
            <div className="col-span-2 flex justify-end">
              <Button type="submit" size="sm" disabled={!nuevo.nombre.trim() || crear.isPending}>
                <Plus />
                {t("Añadir contacto")}
              </Button>
            </div>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
